import React, { useEffect, useRef, useState } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import aboutImage1 from '../assets/mysore.jpg';
import aboutImage2 from '../assets/shimala.jpg';
import aboutImage3 from '../assets/discovery.jpg';
import aboutImage4 from '../assets/sciencepark.jpg';

const AboutUs = () => {
  const scrollRef = useRef(null);
  const [activeTab, setActiveTab] = useState('mission');
  const [isScrolling, setIsScrolling] = useState(true);
  const [aboutData, setAboutData] = useState({
    mission: '',
    approach: '',
    team: '',
    impact: '',
    stats: []
  });
  const [loading, setLoading] = useState(true);

  // Fetch data from Firebase
  useEffect(() => {
    const db = getDatabase();
    const aboutRef = ref(db, 'about');

    const unsubscribe = onValue(aboutRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setAboutData({
          mission: data.mission || '',
          approach: data.approach || '',
          team: data.team || '',
          impact: data.impact || '',
          stats: data.stats || []
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Auto scroll effect with pause on hover
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    let scrollAmount = 0;
    let scrollInterval;

    const startScrolling = () => {
      scrollInterval = setInterval(() => {
        if (scrollContainer && isScrolling) {
          scrollAmount += 0.5;
          scrollContainer.scrollLeft = scrollAmount;

          if (scrollAmount >= scrollContainer.scrollWidth - scrollContainer.clientWidth) {
            scrollAmount = 0;
          }
        }
      }, 20);
    };

    startScrolling();

    return () => clearInterval(scrollInterval);
  }, [isScrolling]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .about-container {
          padding: 40px 20px;
          background: linear-gradient(to bottom, #f8f9fa, #e9f5ff);
          min-height: 100vh;
          font-family: 'Poppins', sans-serif;
          color: #000203ff;
        }

        .about-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .about-title {
          font-size: 2.8rem;
          font-weight: 700;
          color: #023e8a;
          margin-bottom: 15px;
          position: relative;
          display: inline-block;
        }

        .about-title:after {
          content: '';
          display: block;
          width: 100px;
          height: 4px;
          background: linear-gradient(to right, #3498db, #9b59b6);
          margin: 15px auto 0;
          border-radius: 2px;
        }

        .about-subtitle {
          font-size: 1.2rem;
          color: #050505ff;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }

        .content-wrapper {
          max-width: 1200px;
          margin: 0 auto;
        }

        .tabs-container {
          display: flex;
          justify-content: center;
          margin-bottom: 40px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .tab-btn {
          padding: 12px 25px;
          border: none;
          background: #e0e0e0;
          color: #555;
          border-radius: 30px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          font-size: 1rem;
        }

        .tab-btn:hover {
          background: #3498db;
          color: white;
          transform: translateY(-2px);
        }

        .tab-btn.active {
          background: linear-gradient(to right, #3498db, #9b59b6);
          color: white;
          box-shadow: 0 4px 15px rgba(52, 152, 219, 0.3);
        }

        .tab-content {
          background: white;
          border-radius: 15px;
          padding: 30px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          margin-bottom: 40px;
          line-height: 1.8;
          font-size: 1.1rem;
        }

        .tab-content h3 {
          color: #023e8a;
          margin-bottom: 15px;
          font-size: 1.5rem;
        }

        .tab-content p {
          margin-bottom: 15px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin: 40px 0;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          text-align: center;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          transition: transform 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-5px);
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #3498db;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 1rem;
          color: #000607ff;
        }

        .gallery-section {
          margin-top: 60px;
        }

        .section-title {
          font-size: 1.8rem;
          text-align: center;
          margin-bottom: 30px;
          color: #023e8a;
          position: relative;
        }

        .section-title:after {
          content: '';
          display: block;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #3498db, #9b59b6);
          margin: 10px auto 0;
          border-radius: 2px;
        }

        .scroll-gallery {
          display: flex;
          overflow-x: auto;
          scroll-behavior: smooth;
          gap: 25px;
          padding: 20px 10px;
          margin-top: 20px;
          cursor: grab;
        }

        .scroll-gallery:active {
          cursor: grabbing;
        }

        .scroll-gallery::-webkit-scrollbar {
          display: none;
        }

        .gallery-img-container {
          position: relative;
          min-width: 300px;
          height: 200px;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .gallery-img-container:hover {
          transform: scale(1.03);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15);
        }

        .gallery-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .gallery-img-container:hover .gallery-img {
          transform: scale(1.1);
        }

        .img-caption {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 15px;
          background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%);
          color: white;
          font-weight: 500;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .gallery-img-container:hover .img-caption {
          opacity: 1;
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100vh;
        }

        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 5px solid #f3f3f3;
          border-top: 5px solid #3498db;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .about-title {
            font-size: 2rem;
          }

          .about-subtitle {
            font-size: 1rem;
          }

          .tab-btn {
            padding: 10px 20px;
            font-size: 0.9rem;
          }

          .tab-content {
            padding: 20px;
            font-size: 1rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .gallery-img-container {
            min-width: 250px;
            height: 180px;
          }
        }

        @media (max-width: 480px) {
          .about-title {
            font-size: 1.8rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .gallery-img-container {
            min-width: 220px;
            height: 150px;
          }
        }
      `}</style>

      <div className="about-container">
        <div className="about-header">
          <h1 className="about-title">About NMT INDIA HOLIDAYS</h1>
          <p className="about-subtitle">
            Transforming education through immersive travel experiences since 2013
          </p>
        </div>

        <div className="content-wrapper">
          <div className="tabs-container">
            <button 
              className={`tab-btn ${activeTab === 'mission' ? 'active' : ''}`}
              onClick={() => setActiveTab('mission')}
            >
              Our Mission
            </button>
            <button 
              className={`tab-btn ${activeTab === 'approach' ? 'active' : ''}`}
              onClick={() => setActiveTab('approach')}
            >
              Our Approach
            </button>
            <button 
              className={`tab-btn ${activeTab === 'team' ? 'active' : ''}`}
              onClick={() => setActiveTab('team')}
            >
              Our Team
            </button>
            <button 
              className={`tab-btn ${activeTab === 'impact' ? 'active' : ''}`}
              onClick={() => setActiveTab('impact')}
            >
              Our Impact
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'mission' && (
              <div dangerouslySetInnerHTML={{ __html: aboutData.mission }} />
            )}

            {activeTab === 'approach' && (
              <div dangerouslySetInnerHTML={{ __html: aboutData.approach }} />
            )}

            {activeTab === 'team' && (
              <div dangerouslySetInnerHTML={{ __html: aboutData.team }} />
            )}

            {activeTab === 'impact' && (
              <div dangerouslySetInnerHTML={{ __html: aboutData.impact }} />
            )}
          </div>

          {aboutData.stats && aboutData.stats.length > 0 && (
            <div className="stats-grid">
              {aboutData.stats.map((stat, index) => (
                <div key={index} className="stat-card">
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              ))}
            </div>
          )}

          <div className="gallery-section">
            <h3 className="section-title">Learning Journeys in Action</h3>
            <div 
              className="scroll-gallery" 
              ref={scrollRef}
              onMouseEnter={() => setIsScrolling(false)}
              onMouseLeave={() => setIsScrolling(true)}
            >
              <div className="gallery-img-container">
                <img src={aboutImage1} alt="Historical learning" className="gallery-img" />
                <div className="img-caption">Historical Exploration</div>
              </div>
              <div className="gallery-img-container">
                <img src={aboutImage2} alt="Mountain education" className="gallery-img" />
                <div className="img-caption">Mountain Ecology Studies</div>
              </div>
              <div className="gallery-img-container">
                <img src={aboutImage3} alt="Wildlife safari" className="gallery-img" />
                <div className="img-caption">Wildlife Conservation</div>
              </div>
              <div className="gallery-img-container">
                <img src={aboutImage4} alt="Adventure camp" className="gallery-img" />
                <div className="img-caption">Adventure Learning</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;

